import { useApiData } from '@repo/shared/hooks';
import {
	type CancellationErrorResponse,
	type CancellationSuccessResponse,
	getCancellationDetail,
} from '../../../-api';

export interface CancellationErrorWithApiError extends Error {
	apiError: CancellationErrorResponse;
}

/**
 * Custom hook that returns cancellation data with proper type safety.
 * Uses discriminated union to ensure TypeScript knows when data is available.
 *
 * @param bookingId - The booking ID to fetch cancellation details for
 * @returns Discriminated union with guaranteed type safety:
 *   - loading: No data properties available
 *   - error: Error object available, no data
 *   - success: Data guaranteed to exist, no null checks needed
 */
type CancellationDataState =
	| { status: 'loading' }
	| { status: 'error'; error: Error }
	| { status: 'success'; data: CancellationSuccessResponse['data'] | null };

export const useCancellationData = (
	bookingId: string,
	enabled: boolean,
): CancellationDataState => {
	const [{ data: cancellationDetail, status, error }] =
		useApiData<CancellationSuccessResponse>(
			async () => {
				const cancellationDetailResponse =
					await getCancellationDetail(bookingId);

				if ('errors' in cancellationDetailResponse) {
					const error = new Error(
						'Failed to fetch cancellation details',
					) as CancellationErrorWithApiError;
					error.apiError = { errors: cancellationDetailResponse.errors };
					throw error;
				}
				return cancellationDetailResponse;
			},
			[],
			{
				enabled: !!bookingId && enabled,
			},
		);

	// Return discriminated union based on current state
	if (status === 'loading' || status === 'refreshing') {
		return { status: 'loading' };
	}

	if (error) {
		return { status: 'error', error };
	}

	return {
		status: 'success',
		data: cancellationDetail
			? (cancellationDetail as CancellationSuccessResponse).data
			: null,
	};
};
